import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { CallListItem, TurnEntry } from "../../api/calls";
import { distinctModels } from "./call-tech";

const DASH = "—";

export function TechnicalPanel({ rec, turns }: { rec: CallListItem; turns: TurnEntry[] }) {
  const [open, setOpen] = useState(false);
  const models = distinctModels(turns);
  const modelKeys = Object.keys(models);
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderLeft: "2px solid var(--text-muted)",
        borderRadius: "var(--radius-card)",
        padding: "14px 16px",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          letterSpacing: "0.18em",
          color: "var(--text-muted)",
          textTransform: "uppercase",
        }}
      >
        {open ? <ChevronDown size={11} strokeWidth={1.5} /> : <ChevronRight size={11} strokeWidth={1.5} />}
        Technical
      </button>
      {open ? (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }}>
          <TechGroup label="Identifiers">
            <CopyRow label="CALL" value={rec.call_id} />
            <CopyRow label="ENGAGEMENT" value={rec.engagement_id} />
            <CopyRow label="PERSONA" value={rec.persona_id ?? null} />
            <CopyRow label="TARGET" value={rec.target_id ?? null} />
          </TechGroup>
          <TechGroup label="Transport / voice">
            <TechRow label="TRUNK" value={rec.sip_trunk_label ?? null} />
            <TechRow label="LIVEKIT ROOM" value={rec.livekit_room_name ?? null} />
            <TechRow label="PIPELINE" value={rec.voice_pipeline_mode ?? null} />
            <CopyRow label="VOICE ID" value={rec.voice_id ?? null} />
            <TechRow label="LANGUAGE" value={rec.language ?? null} />
          </TechGroup>
          <TechGroup label="Pipeline models">
            {modelKeys.length === 0 ? (
              <TechRow label="MODELS" value={null} />
            ) : (
              modelKeys.map((k) => (
                <TechRow key={k} label={k.toUpperCase()} value={models[k].join(", ")} />
              ))
            )}
          </TechGroup>
        </div>
      ) : null}
    </div>
  );
}

function TechGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9,
          letterSpacing: "0.16em",
          color: "var(--text-muted)",
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>{children}</div>
    </div>
  );
}

function TechRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "baseline",
        padding: "4px 0",
        borderBottom: "1px dashed var(--border-subtle)",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          letterSpacing: "0.1em",
          color: "var(--text-muted)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: value ? "var(--text-primary)" : "var(--text-secondary)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "60%",
        }}
        title={value ?? undefined}
      >
        {value ?? DASH}
      </span>
    </div>
  );
}

function CopyRow({ label, value }: { label: string; value: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <TechRow label={label} value={null} />;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "baseline",
        padding: "4px 0",
        borderBottom: "1px dashed var(--border-subtle)",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          letterSpacing: "0.1em",
          color: "var(--text-muted)",
        }}
      >
        {label}
      </span>
      <button
        type="button"
        title={`${value} — click to copy`}
        onClick={() => {
          void navigator.clipboard?.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        }}
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: copied ? "var(--accent)" : "var(--text-primary)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "60%",
        }}
      >
        {copied ? "copied ✓" : value}
      </button>
    </div>
  );
}
